import { useEffect, useState } from "react";
import GitFitGridLoader from "@/components/GitFitGridLoader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { signOut } from "@/lib/auth";
import { useTheme } from "@/providers/ThemeProvider";
import { Activity, Users, ArrowLeft, LogOut, Eye, EyeOff, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import Leaderboard from "@/components/Leaderboard";
import FollowsList from "@/components/FollowsList";
import ContributionChart from "@/components/ContributionChart";
import MobileBottomNav from "@/components/MobileBottomNav";
import { FooterLinksMenu } from "@/components/FooterLinksMenu";
import { MetricsTour } from "@/components/auth/mobile/MetricsTour";
import UserSearch from "@/components/UserSearch";
import { ReactionFeed } from "@/components/ReactionFeed";
import { AppLogo } from "@/components/AppLogo";
import { SuggestedUsers } from "@/components/SuggestedUsers";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useProfile } from "@/hooks/useProfile";
import { useAppSession } from "@/providers/AppSessionProvider";
import { toast } from "sonner";
import { NotificationPromptDialog } from "@/components/NotificationPromptDialog";
import { useNotificationPromptStore } from "@/stores/notificationPromptStore";
import { detectNativeApp, waitForNotificationsSDK } from "@/lib/platform";
import { oneSignalService } from "@/lib/onesignal";
import { useTutorialStore } from "@/stores/tutorialStore";

const Social = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toggleTheme } = useTheme();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);
  const [metricsTourOpen, setMetricsTourOpen] = useState(false);
  // Initialize as true to prevent flash of expanded feed before collapse logic runs
  const [feedIsCollapsed, setFeedIsCollapsed] = useState(true);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const isMobile = useIsMobile();

  const {
    markSocialVisited,
    shouldShowPromptOnSocialVisit,
    hasShownPrompt
  } = useNotificationPromptStore();

  const isTutorialActive = useTutorialStore((state) => state.isActive);

  // Use centralized session management
  const { ready, session, isAuthenticated } = useAppSession();
  const user = session?.user || null;

  // Redirect to auth if not authenticated (only after session is ready)
  useEffect(() => {
    if (ready !== 'ready') return;

    if (!isAuthenticated) {
      console.log("[Social] No session found, redirecting to auth");
      navigate("/auth", { replace: true });
    }
  }, [ready, isAuthenticated, navigate]);

  // Use centralized profile hook for username check
  const { data: userProfile, isLoading: isLoadingUserProfile } = useProfile(user?.id);

  // Only redirect if profile is loaded and has no username
  useEffect(() => {
    if (ready !== 'ready' || !user || isLoadingUserProfile) return;

    // IMPORTANT: Only redirect if we have a loaded profile that definitely has no username
    // If profile is undefined or null, it might just be loading/refetching - don't redirect
    if (userProfile && userProfile.username === null) {
      console.log("[Social] No username found (username is null), redirecting to auth for onboarding");
      localStorage.removeItem("onboarding_complete");
      navigate("/auth", { replace: true });
    }
  }, [ready, user, userProfile, isLoadingUserProfile, navigate]);

  // Use centralized subscription status
  const { isPremium, isLoading: isLoadingPremium } = useSubscriptionStatus(user?.id);

  // Trigger notification prompt on social tab visit (after 2s delay)
  useEffect(() => {
    if (!user || hasShownPrompt || isTutorialActive) return;

    // Mark that user visited social tab
    markSocialVisited();

    // Check if we should show the prompt
    const shouldShow = shouldShowPromptOnSocialVisit();
    if (!shouldShow) return;

    // Only show in native app
    const checkAndShowPrompt = async () => {
      const detection = await detectNativeApp();
      if (!detection.isNative) {
        console.log('[Social] Not in native app, skipping notification prompt');
        return;
      }

      // Check if we already have permission
      try {
        const sdkLoaded = await waitForNotificationsSDK(5000);
        if (sdkLoaded) {
          const initialized = await oneSignalService.initialize();
          if (initialized) {
            const hasPermission = await oneSignalService.getPermissionStatus();
            if (hasPermission) {
              console.log('[Social] Permission already granted, skipping prompt');
              return;
            }
          }
        }
      } catch (error) {
        console.error('[Social] Error checking permission:', error);
      }

      // Wait 2 seconds after user visits social tab
      setTimeout(() => {
        console.log('[Social] Triggering notification prompt after social visit');
        setShowNotificationPrompt(true);
      }, 2000);
    };

    checkAndShowPrompt();
  }, [user, hasShownPrompt, isTutorialActive, markSocialVisited, shouldShowPromptOnSocialVisit]);

  // Fetch user profile settings (location data only)
  // NOTE: Uses unique query key to avoid cache collision with useProfile hook
  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["profile-settings", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("is_public, city, country")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Keep data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: true, // Always fetch fresh data on mount
  });

  // Track feed loading state for logo shimmer effect
  const { isLoading: isLoadingFeed } = useQuery({
    queryKey: ["feed", user?.id],
    enabled: false, // Don't actually fetch - just track loading state from ReactionFeed
  });

  // Mutation to update profile visibility
  const updateVisibility = useMutation({
    mutationFn: async (isPublic: boolean) => {
      if (!user?.id) throw new Error("User not found");
      const { error } = await supabase.from("profiles_internal").update({ is_public: isPublic }).eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["profile-settings", user?.id] });
      toast.success(t('social.visibility_updated'));
      setShowVisibilityDialog(false);
    },
    onError: () => {
      toast.error(t('social.visibility_failed'));
    },
  });

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const handleSignOutClick = () => {
    if (isMobile) {
      setShowSignOutDialog(true);
    } else {
      handleSignOut();
    }
  };

  if (ready === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <GitFitGridLoader className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('social.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <div className="container max-w-6xl mx-auto py-6 px-4">
        <header className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <AppLogo isLoading={isLoadingFeed} onClick={() => navigate("/")} />
            <div className="flex gap-2">
              {isMobile ? (
                <>
                  <FooterLinksMenu onOpenMetricsTour={() => setMetricsTourOpen(true)} />
                  <Button variant="ghost" size="sm" onClick={toggleTheme} title={t('social.toggle_theme')}>
                    <Moon className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleSignOutClick}>
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('social.dashboard')}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={toggleTheme} title={t('social.toggle_theme')}>
                    <Moon className="w-5 h-5" />
                  </Button>
                  <Button variant="outline" onClick={handleSignOutClick}>
                    {t('social.sign_out')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>

        {!isMobile && (
          <div className="mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            <h2 className="text-xl font-bold">{t('social.title')}</h2>
          </div>
        )}

        <div className="space-y-4">
          <SuggestedUsers currentUserId={user.id} />
          <ReactionFeed userId={user.id} onCollapseChange={setFeedIsCollapsed} />
          <FollowsList userId={user.id} />
          <UserSearch currentUserId={user.id} />
          <Leaderboard
            userId={user.id}
            // Use centralized subscription status
            isPremium={isLoadingPremium ? undefined : isPremium}
            isLoadingPremium={isLoadingPremium}
            isPublic={profile?.is_public}
            userCity={profile?.city ?? undefined}
            userCountry={profile?.country ?? undefined}
            isLoadingProfile={isLoadingProfile}
            onVisibilityDialogOpen={() => setShowVisibilityDialog(true)}
          />
        </div>
      </div>
      <MobileBottomNav />

      {/* Sign Out Confirmation Dialog */}
      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('social.sign_out')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('social.sign_out_confirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('social.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut}>{t('social.sign_out')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profile Visibility Dialog */}
      <Dialog open={showVisibilityDialog} onOpenChange={setShowVisibilityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('social.make_public_title')}</DialogTitle>
            <DialogDescription>
              {t('social.make_public_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              {profile?.is_public ? (
                <>
                  <Eye className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <Label className="font-semibold">{t('social.public_profile')}</Label>
                    <p className="text-sm text-muted-foreground">{t('social.visible_everyone')}</p>
                  </div>
                </>
              ) : (
                <>
                  <EyeOff className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <Label className="font-semibold">{t('social.private_profile')}</Label>
                    <p className="text-sm text-muted-foreground">{t('social.visible_friends')}</p>
                  </div>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVisibilityDialog(false)}>
              {t('social.keep_private')}
            </Button>
            <Button onClick={() => updateVisibility.mutate(true)} disabled={updateVisibility.isPending}>
              {updateVisibility.isPending ? t('social.updating') : t('social.make_public')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Metrics Tour */}
      <MetricsTour
        isOpen={metricsTourOpen}
        onClose={() => setMetricsTourOpen(false)}
      />

      {/* Notification Permission Prompt */}
      <NotificationPromptDialog
        open={showNotificationPrompt}
        onOpenChange={setShowNotificationPrompt}
        onSuccess={() => {
          toast.success(t('social.notifications_enabled'));
        }}
        onError={(error) => {
          // Only show error if it's not a permission denial
          if (!error.includes("denied")) {
            toast.error(error);
          }
        }}
      />
    </div>
  );
};

export default Social;
